import { Heading } from '@ksyos/design-system';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useNavigateInTabBar } from '@frontend/utils/hooks/useNavigateInTabBar';
import type { DataChecksTabs } from './DataChecksTabBar';
import { getSelectedLanguage } from '../../../translate';
import { DataChecksTabBar, DataChecksTabs as DataChecksTabsOptions } from './DataChecksTabBar';
import { OrganizationChecks } from './organizationChecks/OrganizationChecks';
import OrganizationChecksStats from './organizationChecksStats/OrganizationChecksStats';
import { PractitionerChecks } from './practitionerChecks/PractitionerChecks';

export function DataChecks() {
    assertHasAuthorization([
        ManagerAuthorization.practitionerAddEdit,
        ManagerAuthorization.organizationAddEdit,
        ManagerAuthorization.jobs,
    ]);

    const language = getSelectedLanguage();

    const { selectedTab } = useNavigateInTabBar() as { selectedTab: DataChecksTabs };

    const hasOrganizationAddEditRights = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const hasPractitionerAddEditRights = hasOneAuthorization(ManagerAuthorization.practitionerAddEdit);
    const hasJobRights = hasOneAuthorization(ManagerAuthorization.jobs);

    const { organizations, practitioners, organizationStats } = DataChecksTabsOptions;

    const availableTabs = [organizations, practitioners, organizationStats].filter((tab) => {
        if (tab === organizations) {
            return hasOrganizationAddEditRights || hasJobRights;
        }
        if (tab === organizationStats) {
            return hasOrganizationAddEditRights || hasJobRights;
        }
        if (tab === practitioners) {
            return hasPractitionerAddEditRights || hasJobRights;
        }

        return false;
    });

    const activeTab = selectedTab || availableTabs[0];

    function renderTab() {
        switch (activeTab) {
            case practitioners:
                return <PractitionerChecks />;
            case organizations:
                return <OrganizationChecks />;
            default:
                return <OrganizationChecksStats />;
        }
    }

    return (
        <PageTemplate>
            <Heading mb={4}>{language.dataChecks.checks}</Heading>
            <DataChecksTabBar
                availableTabs={availableTabs}
                selectedTab={activeTab}
            />
            {renderTab()}
        </PageTemplate>
    );
}
